import { ApiError } from './errors';
import { isAuthEndpoint } from './headers';

/** A minimal callable matching the shape of an ofetch instance. */
type FetchLike = (request: string, options?: Record<string, unknown>) => Promise<unknown>;

export interface AuthRetryConfig {
  /** Single-flight token refresh. Resolves true if a new token is available. */
  refreshToken: () => Promise<boolean>;
  /** Called when refresh fails — app redirects to its login surface. */
  onAuthFailure: () => void;
}

/**
 * Wraps a fetch instance so a 401 triggers a single-flight refresh and one
 * transparent retry of the original request (with the same options, preserving
 * the idempotency key). Auth endpoints are excluded from the refresh loop.
 * Retrying here — rather than inside an error interceptor — is what allows the
 * request to actually be re-issued.
 */
export function withAuthRetry<T extends FetchLike>(fetchImpl: T, config: AuthRetryConfig): T {
  let refreshInFlight: Promise<boolean> | null = null;

  const wrapped = async (request: string, options?: Record<string, unknown>) => {
    try {
      return await fetchImpl(request, options);
    } catch (error) {
      const is401 =
        error instanceof ApiError && error.kind === 'unauthorized' && error.status === 401;
      if (!is401 || isAuthEndpoint(request)) throw error;

      refreshInFlight ??= config.refreshToken().finally(() => {
        refreshInFlight = null;
      });
      const refreshed = await refreshInFlight;
      if (!refreshed) {
        config.onAuthFailure();
        throw error;
      }
      return await fetchImpl(request, options);
    }
  };

  return wrapped as T;
}
